import { useEffect, useState } from "react";
import { Mic, Loader2, Play, CheckCircle2, XCircle, RotateCw, SkipForward } from "lucide-react";
import { speak } from "@/lib/api";
import { listenOnce, scorePronunciation, speechSupported } from "@/lib/speech";

const sayAsync = (text, slot) =>
  new Promise((resolve) => {
    const u = speak(text, 1, slot);
    if (!u) return resolve();
    u.onend = resolve;
    u.onerror = resolve;
  });

export function RolePlay({ dialogue, onExit }) {
  const speakers = [...new Set(dialogue.map((l) => l.speaker))];
  const [me, setMe] = useState(null);
  const [idx, setIdx] = useState(0);
  const [state, setState] = useState("idle");
  const [results, setResults] = useState({});
  const slotOf = (l) => (l.speaker === speakers[0] ? 0 : 1);
  const line = dialogue[idx];
  const done = me && idx >= dialogue.length;

  useEffect(() => {
    if (!me || done || line.speaker === me) return;
    let alive = true;
    setState("app");
    sayAsync(line.text, slotOf(line)).then(() => { if (alive) { setState("idle"); setIdx((i) => i + 1); } });
    return () => { alive = false; };
  }, [me, idx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => window.speechSynthesis?.cancel(), []);

  const answer = async () => {
    setState("listening");
    try {
      const heard = await listenOnce();
      const r = heard.length ? scorePronunciation(line.text, heard) : { ok: false, heard: "", score: 0 };
      setResults((x) => ({ ...x, [idx]: { ...r, ok: r.score >= 0.6 } }));
    } catch {
      setResults((x) => ({ ...x, [idx]: { ok: false, heard: "", error: true } }));
    }
    setState("idle");
  };

  if (!speechSupported())
    return <p className="text-sm text-slate-600" data-testid="roleplay-unsupported">Ce navigateur ne reconnaît pas la voix (essayez Chrome).</p>;

  if (!me)
    return (
      <div className="space-y-3 rise" data-testid="roleplay-choose">
        <p className="font-display text-2xl font-bold uppercase">Quel rôle jouez-vous ?</p>
        {speakers.map((s, i) => (
          <button key={s} onClick={() => setMe(s)} data-testid={`roleplay-role-${i}`} className="w-full h-14 rounded-xl bg-white border-2 border-slate-200 hover:border-orange-500 font-bold text-lg transition-colors">
            {s} · {dialogue.find((l) => l.speaker === s).role}
          </button>
        ))}
        <button onClick={onExit} data-testid="roleplay-cancel-btn" className="w-full text-sm font-semibold text-slate-500">Annuler</button>
      </div>
    );

  if (done) {
    const mine = Object.values(results);
    const ok = mine.filter((r) => r.ok).length;
    return (
      <div className="space-y-4 text-center rise" data-testid="roleplay-result">
        <p className="font-display text-6xl font-extrabold" data-testid="roleplay-score">{ok}/{dialogue.filter((l) => l.speaker === me).length}</p>
        <p className="text-slate-600">répliques bien dites</p>
        <button onClick={() => { setIdx(0); setResults({}); }} data-testid="roleplay-restart-btn" className="w-full h-12 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold flex items-center justify-center gap-2"><RotateCw className="w-5 h-5" /> Rejouer</button>
        <button onClick={onExit} data-testid="roleplay-exit-btn" className="w-full h-12 rounded-xl border-2 border-slate-300 font-bold">Revenir au dialogue</button>
      </div>
    );
  }

  const r = results[idx];
  return (
    <div className="space-y-4" data-testid="roleplay">
      <p className="text-xs font-bold uppercase text-slate-500" data-testid="roleplay-progress">Réplique {idx + 1} / {dialogue.length} · vous êtes {me}</p>
      <div className={`rounded-2xl border-2 p-4 ${line.speaker === me ? "bg-orange-50 border-orange-400" : "bg-white border-slate-200"}`} data-testid="roleplay-line">
        <div className="text-xs font-bold uppercase text-slate-500">{line.speaker} · {line.role}</div>
        <p className="text-lg font-semibold">{line.text}</p>
      </div>
      {line.speaker !== me ? (
        <p className="flex items-center gap-2 text-slate-600 font-semibold" data-testid="roleplay-app-speaking"><Play className="w-5 h-5 text-orange-500" /> L'application parle…</p>
      ) : (
        <div className="space-y-3">
          <button onClick={answer} disabled={state === "listening"} data-testid="roleplay-mic-btn" className={`mx-auto w-20 h-20 rounded-full grid place-items-center text-white ${state === "listening" ? "bg-red-600 animate-pulse" : "bg-orange-500 hover:bg-orange-600"}`}>
            {state === "listening" ? <Loader2 className="w-9 h-9 animate-spin" /> : <Mic className="w-9 h-9" />}
          </button>
          <p className="text-center text-sm text-slate-600">À vous : dites la réplique</p>
          {r && (
            <div data-testid="roleplay-feedback" className={`rounded-xl px-3 py-2 text-sm font-semibold flex items-center gap-2 ${r.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
              {r.ok ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              <span>{r.ok ? "Bien dit !" : "Presque, réessayez."}{r.heard && <span className="block font-normal">Entendu : « {r.heard} »</span>}</span>
            </div>
          )}
          <button onClick={() => setIdx(idx + 1)} data-testid="roleplay-next-btn" className="w-full h-12 rounded-xl border-2 border-slate-900 font-bold flex items-center justify-center gap-2 hover:bg-slate-900 hover:text-white transition-colors">
            {r ? "Continuer" : "Passer"} <SkipForward className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}
